import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router";

type ModifierKey = "ctrl" | "meta" | "alt" | "shift";
type ShortcutCallback = () => void;

interface Shortcut {
	key: string;
	modifiers?: ModifierKey[];
	callback: ShortcutCallback;
	description?: string;
	enabled?: boolean;
}

interface UseKeyboardShortcutsOptions {
	shortcuts: Shortcut[];
	enabled?: boolean;
}

function isInputElement(target: HTMLElement): boolean {
	return target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable;
}

function checkModifiers(event: KeyboardEvent, modifiers: ModifierKey[]): boolean {
	const ctrlMatch = modifiers.includes("ctrl") === (event.ctrlKey || event.metaKey);
	const altMatch = modifiers.includes("alt") === event.altKey;
	const shiftMatch = modifiers.includes("shift") === event.shiftKey;
	return ctrlMatch && altMatch && shiftMatch;
}

function hasUnwantedModifiers(event: KeyboardEvent): boolean {
	return event.ctrlKey || event.metaKey || event.altKey;
}

function matchesShortcut(event: KeyboardEvent, shortcut: Shortcut): boolean {
	if (shortcut.enabled === false) {
		return false;
	}

	const keyMatch = event.key.toLowerCase() === shortcut.key.toLowerCase();
	if (!keyMatch) {
		return false;
	}

	const modifiers = shortcut.modifiers || [];

	// For shortcuts without modifiers, ensure no modifiers are pressed
	if (modifiers.length === 0 && hasUnwantedModifiers(event)) {
		return false;
	}

	return checkModifiers(event, modifiers);
}

function shouldIgnoreEvent(event: KeyboardEvent): boolean {
	const target = event.target as HTMLElement;
	const isEscape = event.key === "Escape";
	return isInputElement(target) && !isEscape;
}

export function useKeyboardShortcuts({ shortcuts, enabled = true }: UseKeyboardShortcutsOptions) {
	const handleKeyDown = useCallback(
		(event: KeyboardEvent) => {
			if (!enabled || shouldIgnoreEvent(event)) {
				return;
			}

			for (const shortcut of shortcuts) {
				if (matchesShortcut(event, shortcut)) {
					event.preventDefault();
					shortcut.callback();
					break;
				}
			}
		},
		[shortcuts, enabled],
	);

	useEffect(() => {
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [handleKeyDown]);
}

// =============================================================================
// Global Navigation Shortcuts Hook
// =============================================================================

interface UseGlobalShortcutsOptions {
	onToggleSidebar?: () => void;
	onOpenSearch?: () => void;
	onOpenShortcutsHelp?: () => void;
}

export function useGlobalShortcuts({
	onToggleSidebar,
	onOpenSearch,
	onOpenShortcutsHelp,
}: UseGlobalShortcutsOptions = {}) {
	const navigate = useNavigate();

	const shortcuts: Shortcut[] = [
		// Navigation
		{
			key: "d",
			modifiers: ["ctrl"],
			callback: () => navigate("/dashboard"),
			description: "Go to Dashboard",
		},
		{
			key: "a",
			modifiers: ["ctrl"],
			callback: () => navigate("/assets"),
			description: "Go to Assets",
		},
		{
			key: "l",
			modifiers: ["ctrl"],
			callback: () => navigate("/locations"),
			description: "Go to Locations",
		},
		// Actions
		{
			key: "b",
			modifiers: ["ctrl"],
			callback: () => onToggleSidebar?.(),
			description: "Toggle sidebar",
			enabled: !!onToggleSidebar,
		},
		{
			key: "k",
			modifiers: ["ctrl"],
			callback: () => onOpenSearch?.(),
			description: "Open search",
			enabled: !!onOpenSearch,
		},
		{
			key: "/",
			modifiers: ["ctrl"],
			callback: () => onOpenShortcutsHelp?.(),
			description: "Show shortcuts",
			enabled: !!onOpenShortcutsHelp,
		},
		{
			key: "?",
			modifiers: ["shift"],
			callback: () => onOpenShortcutsHelp?.(),
			description: "Show shortcuts",
			enabled: !!onOpenShortcutsHelp,
		},
	];

	useKeyboardShortcuts({ shortcuts });
}

// =============================================================================
// Shortcut definitions for help modal
// =============================================================================

export const shortcutDefinitions = {
	navigation: [
		{ keys: ["Ctrl", "D"], description: "shortcuts.goToDashboard" },
		{ keys: ["Ctrl", "A"], description: "shortcuts.goToAssets" },
		{ keys: ["Ctrl", "L"], description: "shortcuts.goToLocations" },
		{ keys: ["Ctrl", "B"], description: "shortcuts.toggleSidebar" },
	],
	actions: [
		{ keys: ["Ctrl", "K"], description: "shortcuts.search" },
		{ keys: ["Ctrl", "S"], description: "shortcuts.save" },
		{ keys: ["Esc"], description: "shortcuts.cancel" },
	],
	general: [
		{ keys: ["Ctrl", "/"], description: "shortcuts.help" },
		{ keys: ["?"], description: "shortcuts.help" },
	],
};
